import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Send, Paperclip, Loader, Search, ArrowUpRight, RotateCcw, FileText } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getAccessToken } from '../services/auth';
import {
  prepareAttachment, blocksFor, forStorage, filesFromDrop,
  dragCarriesFiles, explainSendFailure, MAX_ATTACH_BYTES, prettySize,
} from '../lib/chatAttachments';

// The board-level chat, as opposed to TaskCoach, which opens on one task and stays
// there. This one is for the person who owns the board and arrives with a question
// rather than a task in hand: "what did we decide about the rent split", "read this
// statement and tell me what changed", "put that on Greg's list".
//
// Two things make it more than a chat window:
//
//   1. It can look through its own board. search_the_board hits {prefix}_items and
//      {prefix}_notes, so the answer comes from what the team actually wrote down
//      rather than from whatever happens to fit in a system prompt.
//   2. It can hand work back. send_to_greg writes a real row to greg_backlog, the
//      same list every morning brief reads. Without that, an answer ending in
//      "someone should look at this" goes nowhere.
//
// Deliberately NOT here yet: running SQL. That waits until each domain has read-only
// views worth pointing at, behind a Postgres role that cannot write. Handing a chat
// arbitrary SQL against the live database is a different risk class to reading a
// board it already owns, and it is not needed to make this useful this week.

const MODEL = 'claude-sonnet-4-6'; // the only model claude-proxy allows

const MAX_ATTACHMENTS = 4;
// Only recent turns carry their bytes upstream. Older attachments stay in the
// transcript by name, so a long conversation cannot grow past that cap.
const BYTES_HISTORY_TURNS = 6;
// A hard stop on the tool loop. Four rounds is plenty for "search, then answer";
// more than that is a model going in circles, and each round is a live API call.
const MAX_TOOL_ROUNDS = 4;

// claude-proxy refuses a conversation over 60 messages, and every tool round adds
// two of them, so a long working thread would start failing with a flat 400. Send a
// window instead of the whole transcript.
const MAX_SEND_MESSAGES = 40;

// A window has to start on a real question. Slicing between an assistant tool_use
// and the tool_result that answers it leaves an orphan the API rejects outright, so
// walk the start forward to the first user message that is not a tool result.
function windowFor(history) {
  if (history.length <= MAX_SEND_MESSAGES) return history;
  let start = history.length - MAX_SEND_MESSAGES;
  while (start < history.length) {
    const m = history[start];
    const blocks = typeof m.content === "string" ? [] : (m.content || []);
    if (m.role === "user" && !blocks.some((b) => b.type === "tool_result")) break;
    start += 1;
  }
  return start >= history.length ? history.slice(-2) : history.slice(start);
}

// Every tool_use has to be answered by a tool_result in the very next message, or the
// API rejects the whole request - and because the transcript is stored, it rejects
// every request after that too. Rachael, 30 Sep: a handoff turn ran out of rounds on
// two write_it_down calls, they were saved unanswered, and the chat said "Claude API
// error" to everything from then on. Answer any orphan here, on the way out, so a
// transcript broken that way heals itself on the next send.
const NOT_RUN = 'Not run: that turn ran out of steps before this could happen, so nothing was saved. Do it again if it is still wanted.';
function repairToolPairs(history) {
  const out = [];
  for (let i = 0; i < history.length; i++) {
    const m = history[i];
    out.push(m);
    const blocks = typeof m.content === 'string' ? [] : (m.content || []);
    const ids = m.role === 'assistant' ? blocks.filter((b) => b.type === 'tool_use').map((b) => b.id) : [];
    if (!ids.length) continue;
    const next = history[i + 1];
    const nextBlocks = !next || next.role !== 'user' ? []
      : typeof next.content === 'string' ? [{ type: 'text', text: next.content }] : (next.content || []);
    const answered = new Set(nextBlocks.filter((b) => b.type === 'tool_result').map((b) => b.tool_use_id));
    const missing = ids.filter((id) => !answered.has(id))
      .map((id) => ({ type: 'tool_result', tool_use_id: id, content: NOT_RUN }));
    if (!missing.length) continue;
    if (next && next.role === 'user') {
      // Results first: the API wants them ahead of anything else in the message.
      const results = nextBlocks.filter((b) => b.type === 'tool_result');
      const rest = nextBlocks.filter((b) => b.type !== 'tool_result');
      out.push({ ...next, content: [...results, ...missing, ...rest] });
      i += 1;
    } else {
      out.push({ role: 'user', content: missing });
    }
  }
  return out;
}

const TOOLS = [
  {
    name: 'search_the_board',
    description:
      'Search this board for anything already written down: tasks, phases, findings, ' +
      'open questions, the step by step instructions on a task, and notes people have ' +
      'left. Use it before answering any question about what was decided, what is ' +
      'outstanding, who owns something, or how something is done here. Prefer one or ' +
      'two distinctive words over a long phrase.',
    input_schema: {
      type: 'object',
      properties: { q: { type: 'string', description: 'A word or short phrase to look for.' } },
      required: ['q'],
    },
  },
  {
    name: 'send_to_greg',
    description:
      "Put something on Greg's list. Use this when the conversation turns up work that " +
      'is his to decide or do: a question only he can answer, a number that looks wrong, ' +
      'a decision that is blocking someone. It writes a real item he will see in his ' +
      'morning brief. Say so in your reply after you use it, and do not use it for ' +
      'anything the person you are talking to can do themselves.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'One line, written as the action he needs to take.' },
        detail: { type: 'string', description: 'The background he needs to act on it without coming back to ask.' },
      },
      required: ['title'],
    },
  },
  {
    name: 'write_it_down',
    description:
      'Record something worth keeping on this board so it outlives this conversation. ' +
      'Use it whenever someone explains how something works here, corrects a wrong ' +
      'assumption, settles a question, or tells you a number and where it came from. ' +
      'A finding goes on the What we have learned tab and is what the next person and ' +
      'the next Claude will read. Do this without being asked: the person talking to ' +
      'you should not have to think about where knowledge is stored.',
    input_schema: {
      type: 'object',
      properties: {
        what: { type: 'string', description: 'The heading. One clear sentence saying the thing itself.' },
        why: { type: 'string', description: 'The detail: how it was established, what it means, what to do with it.' },
        kind: { type: 'string', enum: ['finding', 'note'], description: 'finding for durable knowledge, note for a passing remark.' },
        phase: { type: 'string', description: 'Optional phase to file it under.' },
      },
      required: ['what'],
    },
  },
  {
    name: 'park_a_loose_end',
    description:
      'Store something you noticed that is true but not worth interrupting them for ' +
      'right now. Nobody sees it. Use this instead of adding a things-to-flag list to ' +
      'your answer, so the observation is kept without costing them attention. Give it a ' +
      'surfaceWhen: the condition in plain words that should bring it back.',
    input_schema: {
      type: 'object',
      properties: {
        what: { type: 'string', description: 'The observation, one or two sentences.' },
        where: { type: 'string', description: 'Where it lives: a screen, a table, a task number.' },
        why: { type: 'string', description: 'Why it might matter later.' },
        severity: { type: 'string', enum: ['low', 'medium', 'high'] },
        surfaceWhen: { type: 'string', description: 'What should bring this back up.' },
      },
      required: ['what'],
    },
  },
];

const textOf = (content) => {
  if (typeof content === 'string') return content;
  return (content || []).filter((b) => b.type === 'text').map((b) => b.text).join('\n').trim();
};

// Strip attachment bytes out of a message. Used both for what we store (base64 in a
// JSONB column we read on every open is a slow leak) and for older turns in the
// request. The name stays, so the thread still reads correctly.
function stripBytes(msg) {
  if (typeof msg.content === 'string') return msg;
  const kept = [];
  const dropped = [];
  for (const b of msg.content || []) {
    if (b.type === 'image' || b.type === 'document') dropped.push(b.title || 'an attachment');
    else kept.push(b);
  }
  if (!dropped.length) return msg;
  const note = `(shared earlier: ${dropped.join(', ')})`;
  const hasText = kept.some((b) => b.type === 'text');
  if (hasText) {
    return { ...msg, content: kept.map((b) => (b.type === 'text' ? { ...b, text: `${b.text}\n${note}` } : b)) };
  }
  return { ...msg, content: [...kept, { type: 'text', text: note }] };
}

// extraTools / runExtraTool let a board add something only it can answer. Marketing
// uses it to read its own headline numbers, because its board is a dashboard rather
// than a task list. Everything else runs on the two shared tools above.
export default function BrainChat({ config, onChanged, onClose, extraTools = [], runExtraTool }) {
  const cfg = config;
  const q = useCallback((op) => `${cfg.prefix}_${op}`, [cfg.prefix]);

  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [thinking, setThinking] = useState('');
  const [busy, setBusy] = useState(false); // a big file on its way to R2
  const [err, setErr] = useState('');
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  // Attachments arrive one await at a time, so the room-left count has to read a
  // ref rather than the state captured when the drop started.
  const attachRef = useRef([]);
  useEffect(() => { attachRef.current = attachments; }, [attachments]);

  useEffect(() => {
    let alive = true;
    neonQuery(q('board_chat_get'), {})
      .then((d) => { if (alive) setMessages(Array.isArray(d?.messages) ? d.messages : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [q]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);

  // Bytes never reach the database. An attachment is stored as its name plus, if it
  // went to R2, its key, so a document is still readable when the chat is reopened
  // tomorrow on another machine.
  const persist = (list) => {
    const clean = list.map((m) => {
      const out = stripBytes(m);
      return m.docs ? { ...out, docs: m.docs.map(forStorage) } : out;
    });
    neonQuery(q('board_chat_save'), { messages: clean }).catch(() => {});
  };

  const startFresh = async () => {
    setMessages([]); setErr('');
    try { await neonQuery(q('board_chat_clear'), {}); } catch (e) { /* the screen is already clear */ }
  };

  // -- attachments -----------------------------------------------------------
  // Anything she can drop is worth taking. A PDF, a spreadsheet, a bank download
  // and a screenshot all end up as something Claude can read; a file type nobody
  // can read is kept anyway and says so, rather than being silently refused.
  const takeFiles = async (list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) {
      setErr('The browser did not hand over anything it can read. That happens with a picture dragged out of an email body, which is not a file. or use the paperclip.');
      return;
    }
    setErr('');
    const room = MAX_ATTACHMENTS - attachRef.current.length;
    if (room <= 0) {
      setErr(`That is as many files as one message will take (${MAX_ATTACHMENTS}). Send this one first.`);
      return;
    }
    setBusy(true);
    try {
      for (const f of incoming.slice(0, room)) {
        try {
          const att = await prepareAttachment(f, { prefix: 'accounting' });
          attachRef.current = [...attachRef.current, att];
          setAttachments(attachRef.current);
          if (att.note) setErr(att.note);
        } catch (e) { setErr(e.message); }
      }
      if (incoming.length > room) setErr(`Only the first ${room} went on. Send this message, then add the rest.`);
    } finally { setBusy(false); }
  };

  // -- drag and drop ---------------------------------------------------------
  // Mercedes, 21 Aug: "when I drop a file into the chat, it does not upload,
  // nothing happens". Three things were wrong and all three are fixed here.
  //
  // The window listeners are the important one. Without them a drop that lands a
  // few pixels off the panel makes Chrome navigate away and open the file, which
  // throws the page and the conversation away. From the far side of the screen
  // that is indistinguishable from "nothing happened".
  //
  // Chrome also refuses the drop outright unless dropEffect is set on EVERY
  // dragover, and the drag has to be recognised as carrying files at all, so a
  // dragged selection of text no longer arms the panel for nothing.
  useEffect(() => {
    const swallow = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const onDragEnter = (e) => {
    if (!dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => {
    if (!dragCarriesFiles(e.dataTransfer)) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'copy'; } catch (_) { /* Firefox is fussy, the drop still lands */ }
    if (!dragging) setDragging(true);
  };
  // Counted, not toggled: dragging across a child fires leave on the one behind it.
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  // Async on purpose: an Outlook attachment has to be pulled out of the message
  // before it is a file at all. filesFromDrop does the synchronous half of that
  // before it awaits anything, which is the whole trick.
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    takeFiles(await filesFromDrop(e.dataTransfer));
  };

  // -- the tool loop ---------------------------------------------------------
  const runTool = async (name, input) => {
    if (name === 'search_the_board') {
      const d = await neonQuery(q('search'), { q: String(input?.q || '') });
      const items = d?.items || [];
      const notes = d?.notes || [];
      if (!items.length && !notes.length) return `Nothing on the board matches "${input?.q}".`;
      const lines = [];
      if (items.length) {
        lines.push('ITEMS:');
        for (const i of items) {
          lines.push(`#${i.id} [${i.kind}/${i.status}${i.owner ? ', ' + i.owner : ''}] ${i.title}`);
          if (i.detail) lines.push(`  ${i.detail}`);
          if (i.how_to) lines.push(`  instructions: ${String(i.how_to).slice(0, 600)}`);
        }
      }
      if (notes.length) {
        lines.push('NOTES:');
        for (const n of notes) {
          lines.push(`- ${n.author || 'someone'} on ${n.item_title || 'the board'}: ${n.body}`);
        }
      }
      return lines.join('\n');
    }
    if (name === 'send_to_greg') {
      const d = await neonQuery(q('handoff'), { title: input?.title, detail: input?.detail || '' });
      if (onChanged) onChanged();
      return d?.ok
        ? `Added to Greg's list as item ${d.backlogId}. It will show in his brief.`
        : 'That did not save. Tell them to raise it with Greg directly.';
    }
    if (name === 'write_it_down') {
      const d = await neonQuery(q('remember'), {
        what: input?.what, why: input?.why, kind: input?.kind, phase: input?.phase,
      });
      if (onChanged) onChanged();
      return d?.ok ? `Written down as a ${d.kind} on this board.` : 'Could not write that down.';
    }
    if (name === 'park_a_loose_end') {
      const d = await neonQuery(q('park'), {
        what: input?.what, where: input?.where, why: input?.why,
        severity: input?.severity, surfaceWhen: input?.surfaceWhen,
      });
      return d?.ok ? 'Parked. They will not see it until it matters.' : 'Could not park that.';
    }
    if (runExtraTool) {
      const out = await runExtraTool(name, input);
      if (out != null) return out;
    }
    return `There is no tool called ${name}.`;
  };

  const systemPrompt = [
    `You are the ${cfg.title} for The Crab Place, a family seafood business in Crisfield, Maryland.`,
    `You are talking to whoever runs ${cfg.sectionLabel.toLowerCase()} here, inside their own admin tool.`,
    'This board is theirs to build. Treat what they tell you as something to record and act on, not just to answer.',
    '',
    'How to work:',
    '- Search the board before answering anything about what was decided, what is outstanding, or how something is done here. What the team wrote down beats what you can infer.',
    '- When they tell you something worth keeping, write it down without being asked. How a',
    'process actually works, a correction to something we had wrong, a number and where it',
    'came from, a decision and the reason for it. They should not have to think about where',
    'knowledge lives. Say in one short line that you did it.',
    '- Keep answers short and plain. No jargon without saying what it means in the same breath.',
    '- If you are not sure, say so, and say who would know.',
    '- They can attach screenshots, PDFs, spreadsheets and bank downloads. Read what is actually in the file and answer about that, rather than describing it back to them.',
    '- If a message says a file was attached but not readable, say plainly what to do (save it as a PDF, or export it to CSV) instead of guessing at what it contained.',
    "- When something is Greg's to decide, put it on his list rather than leaving it in the conversation, and tell them you did.",
    '- Do not use em dashes or en dashes. Use commas, periods or brackets.',
    '',
    'Finishing something means finishing it. End on the outcome and stop. Do not add a',
    'list of things to flag, things to watch, or things worth knowing. That habit reads as',
    'thoroughness and lands as extra load on somebody already carrying a lot.',
    '',
    'When you notice something true that is not worth interrupting them for, use',
    'park_a_loose_end. It is stored for later and they never see it, so nothing is lost by',
    'staying quiet. Raise it out loud ONLY if it beats the thing they are working on right',
    'now. Not if it is merely interesting, or true, or something a careful person would',
    'mention.',
    '',
    'Three things always get said out loud, because staying quiet about them cannot be',
    'undone: money about to move wrongly, customer data exposed, and a number they are',
    'about to act on being wrong.',
    cfg.chatGuidance || '',
    cfg.prefix === 'acct'
      ? 'Never tell anyone to change, delete or post anything in QuickBooks. This work is read and export only. If something needs changing, it goes to Greg.'
      : '',
  ].filter(Boolean).join('\n');

  // Attachments live on the message as descriptors, not as content blocks, and the
  // blocks are built here on the way out. That is what lets a document stored in R2
  // get a FRESH signed URL on every call: the signature lasts an hour, and a
  // conversation about the same statement can easily outlive that.
  const toWire = async (m, recent) => {
    const base = typeof m.content === 'string' ? [{ type: 'text', text: m.content }] : (m.content || []);
    const docs = m.docs || [];
    if (!docs.length) return { role: m.role, content: base };
    if (!recent) {
      return {
        role: m.role,
        content: [...base, { type: 'text', text: `(shared earlier: ${docs.map((d) => d.name).join(', ')})` }],
      };
    }
    const lead = [];
    for (const d of docs) {
      try { lead.push(...(await blocksFor(d))); }
      catch (_) { lead.push({ type: 'text', text: `(${d.name} could not be opened just now)` }); }
    }
    // tool_results stay at the front, ahead of any document this message carries.
    const results = base.filter((b) => b.type === 'tool_result');
    const rest = base.filter((b) => b.type !== 'tool_result');
    return { role: m.role, content: [...results, ...lead, ...rest] };
  };

  const callClaude = async (history) => {
    const sent = repairToolPairs(windowFor(history));
    const withBytes = await Promise.all(
      sent.map((m, i) => toWire(stripBytes(m), i >= sent.length - BYTES_HISTORY_TURNS))
    );
    const res = await fetch('/.netlify/functions/claude-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
      body: JSON.stringify({
        model: MODEL,
        max_tokens: 1400,
        system: systemPrompt,
        tools: [...TOOLS, ...extraTools],
        messages: withBytes,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Claude is not answering (${res.status}).`);
    return data;
  };

  const send = async () => {
    const text = draft.trim();
    const docs = attachments;
    if ((!text && !docs.length) || thinking || busy) return;
    setDraft(''); setAttachments([]); attachRef.current = []; setErr('');

    // A file we cannot read is still named in the message, so the answer is "I
    // cannot open a .docx, save it as a PDF" rather than a reply that quietly
    // pretends nothing was attached.
    const unreadable = docs.filter((d) => !d.readable).map((d) => d.name);
    const body = [
      text || 'Have a look at this.',
      unreadable.length ? `(also attached, but not readable: ${unreadable.join(', ')})` : '',
    ].filter(Boolean).join('\n');

    let history = [...messages, { role: 'user', content: [{ type: 'text', text: body }], docs }];
    setMessages(history);
    setThinking('Thinking');

    try {
      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        const data = await callClaude(history);
        history = [...history, { role: 'assistant', content: data.content || [] }];
        setMessages(history);

        const calls = (data.content || []).filter((b) => b.type === 'tool_use');
        if (data.stop_reason !== 'tool_use' || !calls.length) { persist(history); break; }

        // A board's own tools can say what they are doing (cfg.toolLabels), so reading
        // the ad account does not sit under "Looking through the board" for 15 seconds.
        const ownLabel = calls.map((c) => (cfg.toolLabels || {})[c.name]).find(Boolean);
        setThinking(calls.some((c) => c.name === 'send_to_greg') ? "Adding to Greg's list" : (ownLabel || 'Looking through the board'));
        const results = [];
        for (const c of calls) {
          let out;
          try { out = await runTool(c.name, c.input || {}); }
          catch (e) { out = `That did not work: ${e.message}`; }
          results.push({ type: 'tool_result', tool_use_id: c.id, content: String(out).slice(0, 12000) });
        }
        history = [...history, { role: 'user', content: results }];
        setMessages(history);
        setThinking('Thinking');

        // Out of rounds with a tool result still unanswered: ask once more without
        // looping, so the conversation never ends on a dangling tool call.
        // If that last answer still asks for tools, run them anyway rather than drop
        // them: they are usually the write_it_down / send_to_greg the whole turn was
        // building toward, and an unanswered tool_use poisons every later request.
        if (round === MAX_TOOL_ROUNDS - 1) {
          const final = await callClaude(history);
          history = [...history, { role: 'assistant', content: final.content || [] }];
          const late = (final.content || []).filter((b) => b.type === 'tool_use');
          if (late.length) {
            const lateResults = [];
            for (const c of late) {
              let out;
              try { out = await runTool(c.name, c.input || {}); }
              catch (e) { out = `That did not work: ${e.message}`; }
              lateResults.push({ type: 'tool_result', tool_use_id: c.id, content: String(out).slice(0, 12000) });
            }
            history = [...history, { role: 'user', content: lateResults }];
          }
          setMessages(history);
          persist(history);
        }
      }
    } catch (e) {
      const said = explainSendFailure(e.message, docs.length > 0);
      setErr(said);
      const next = [...history, { role: 'assistant', content: [{ type: 'text', text: `I could not answer just then: ${said}` }] }];
      setMessages(next);
      persist(next);
    } finally { setThinking(''); }
  };

  // -- render ----------------------------------------------------------------
  const bubbles = [];
  for (const m of messages) {
    const blocks = typeof m.content === 'string' ? [] : (m.content || []);
    if (blocks.some((b) => b.type === 'tool_result')) continue; // the answer that follows is what they read
    const t = textOf(m.content);
    const files = [
      ...(m.docs || []).map((d) => d.name),
      // Older transcripts, from before attachments moved off the content blocks.
      ...blocks.filter((b) => b.type === 'image' || b.type === 'document').map((b) => b.title || 'attachment'),
    ];
    const tools = blocks.filter((b) => b.type === 'tool_use');
    if (!t && !files.length && !tools.length) continue;
    bubbles.push({ role: m.role, text: t, files, tools });
  }

  return (
    // The drag handlers sit on the backdrop as well as the panel, so a drop
    // anywhere over the open chat counts. Aiming at a 760px strip while holding a
    // file is harder than it sounds.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,20,40,.5)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
      onClick={onClose}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}>
      <div onClick={(e) => e.stopPropagation()}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={(e) => {
          if (!dragCarriesFiles(e.clipboardData)) return;
          e.preventDefault();
          filesFromDrop(e.clipboardData).then(takeFiles);
        }}
        style={{ position: 'relative', width: 'min(760px,100%)', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>

        {dragging && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none',
            background: 'rgba(255,246,234,.94)', border: '3px dashed var(--orange)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <Paperclip size={38} color="var(--orange)" />
            <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--navy)' }}>Drop it here</div>
            <div style={{ fontSize: 14, color: '#3d4b58' }}>
              PDFs, spreadsheets, statements, screenshots. Up to {prettySize(MAX_ATTACH_BYTES)}.
            </div>
          </div>
        )}

        <div style={{ background: 'var(--navy)', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9dc4dd' }}>Ask the brain</div>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{cfg.title}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={startFresh} title="Start a fresh conversation"
              style={{ background: 'none', border: 'none', color: '#9dc4dd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
              <RotateCcw size={14} /> Start fresh
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={20} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', background: '#f8fafc' }}>
          {!loaded && <div style={{ color: '#8b94a3' }}>Picking up where you left off...</div>}

          {loaded && !bubbles.length && (
            <div style={{ color: '#5a6b7a', lineHeight: 1.7, fontSize: 15 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
                This is your working chat for {cfg.sectionLabel.toLowerCase()}.
              </div>
              It can read everything already on this board, and it can put things on Greg's list when they are his call. Try:
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                {(cfg.examples || []).map((ex) => <li key={ex}>{ex}</li>)}
                <li>What is still open, and who is waiting on me?</li>
                <li>Read this statement and tell me what changed from last month.</li>
                <li>Remind me how we handle this, then write it down as a finding.</li>
              </ul>
              Drag in a PDF, a spreadsheet, a bank download or a screenshot any time, or paste
              with Ctrl+V. Files up to {prettySize(MAX_ATTACH_BYTES)} are fine.
            </div>
          )}

          {bubbles.map((b, i) => (
            <div key={i} style={{ marginBottom: 14, display: 'flex', justifyContent: b.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '86%', padding: '10px 13px', borderRadius: 10, fontSize: 14.8, lineHeight: 1.6,
                background: b.role === 'user' ? 'var(--navy)' : '#fff',
                color: b.role === 'user' ? '#fff' : '#22303c',
                border: b.role === 'user' ? 'none' : '1px solid #dfe7ee',
              }}>
                {b.tools.map((t, k) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#6b7a89', marginBottom: b.text ? 8 : 0 }}>
                    {t.name === 'send_to_greg' ? <ArrowUpRight size={13} /> : <Search size={13} />}
                    {t.name === 'send_to_greg'
                      ? `Put on Greg's list: ${t.input?.title || ''}`
                      : `Searched the board for "${t.input?.q || ''}"`}
                  </div>
                ))}
                {!!b.files.length && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: b.text ? 8 : 0 }}>
                    {b.files.map((f, k) => (
                      <span key={k} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '2px 7px', borderRadius: 20,
                        background: b.role === 'user' ? 'rgba(255,255,255,.16)' : '#eef3f8',
                      }}><FileText size={12} /> {f}</span>
                    ))}
                  </div>
                )}
                {b.text && (b.role === 'assistant'
                  ? <div className="markdown-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{b.text}</ReactMarkdown></div>
                  : <div style={{ whiteSpace: 'pre-wrap' }}>{b.text}</div>)}
              </div>
            </div>
          ))}

          {thinking && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7a89', fontSize: 13.5 }}>
              <Loader size={14} /> {thinking}...
            </div>
          )}
          <div ref={endRef} />
        </div>

        {err && <div style={{ padding: '8px 20px', background: '#fdecea', color: '#8a2018', fontSize: 13.5 }}>{err}</div>}

        <div style={{ borderTop: '1px solid #e3eaf1', padding: '10px 20px 14px', background: '#fff' }}>
          {!!attachments.length && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {attachments.map((a, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, padding: '3px 8px', borderRadius: 20,
                  background: a.readable ? '#eef3f8' : '#fdf3e6', color: '#3d4b58',
                }} title={a.readable ? (a.stored ? 'Saved, and I will read it' : 'I will read this') : 'Saved, but I cannot read inside this one'}>
                  <FileText size={12} /> {a.name}
                  {!a.readable && <span style={{ fontSize: 11, color: '#a35a12' }}>not readable</span>}
                  <button onClick={() => setAttachments((list) => list.filter((_, k) => k !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b94a3', display: 'flex' }}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          {busy && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#6b7a89', fontSize: 13, marginBottom: 8 }}>
              <Loader size={13} /> Taking the file in...
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button className="btn btn-ghost" title="Attach a PDF, a spreadsheet or a screenshot"
              onClick={() => fileRef.current?.click()} style={{ padding: '8px 10px' }}>
              <Paperclip size={17} />
            </button>
            {/* No accept filter. Anything she can pick is either read or kept with a
                plain reason why not, and an accept list is one more way for a file
                she needs to be invisible in the picker. */}
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
              onChange={(e) => { takeFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
            <textarea
              className="form-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything, or drop a document in"
              rows={draft.length > 120 ? 4 : 2}
              style={{ flex: 1, resize: 'none' }}
            />
            <button className="btn btn-primary" onClick={() => send()} disabled={!!thinking || busy || (!draft.trim() && !attachments.length)}
              style={{ padding: '9px 14px' }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
